import { useEffect, useMemo } from "react";
import { GraduationCap } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  ONBOARDING_TASK_TYPE,
  OnboardingTask,
  Permission,
} from "@forgepoint/shared";
import { useAuthStore, useHasPermission } from "@/store/authStore";
import { useAgentTasksStore } from "@/store/agentTasksStore";
import { useOnboardingStore } from "@/modules/onboarding/onboardingStore";

/**
 * Loads the onboarding board (when the user can view it) and returns the open
 * onboarding tasks the inbox should count as pending: the signed-in user's
 * tasks in "My inbox", all open tasks (honoring the assignee filter) in
 * "Company inbox", none while an opportunity or custom field filter is active.
 */
export function useOnboardingInboxTasks(): OnboardingTask[] {
  const canView = useHasPermission(Permission.ONBOARDING_VIEW);
  const currentUserId = useAuthStore((state) => state.user?.id);
  const viewMode = useAgentTasksStore((state) => state.viewMode);
  const filterUserId = useAgentTasksStore((state) => state.filterUserId);
  const filterOpportunityId = useAgentTasksStore(
    (state) => state.filterOpportunityId,
  );
  const hasCustomFieldFilter = useAgentTasksStore(
    (state) => Object.keys(state.filterCustomFields).length > 0,
  );
  const taskMap = useOnboardingStore((state) => state.taskMap);
  const fetchBoard = useOnboardingStore((state) => state.fetchBoard);

  useEffect(() => {
    if (canView) {
      fetchBoard();
    }
  }, [canView, fetchBoard]);

  return useMemo(() => {
    if (!canView || filterOpportunityId || hasCustomFieldFilter) {
      return [];
    }
    return [...taskMap.values()]
      .filter((task) => !task.completed)
      .filter((task) =>
        viewMode === "USER"
          ? task.assigneeUserId === currentUserId
          : !filterUserId || task.assigneeUserId === filterUserId,
      )
      .sort((a, b) => a.order - b.order);
  }, [
    canView,
    filterOpportunityId,
    hasCustomFieldFilter,
    taskMap,
    viewMode,
    currentUserId,
    filterUserId,
  ]);
}

/**
 * Filters onboarding tasks against the inbox search query, matching the task
 * name, section, and description.
 */
export function filterOnboardingTasks(
  tasks: OnboardingTask[],
  searchQuery: string,
): OnboardingTask[] {
  const searchLower = searchQuery.toLowerCase();
  if (!searchLower) {
    return tasks;
  }
  return tasks.filter(
    (task) =>
      task.name.toLowerCase().includes(searchLower) ||
      (task.section ?? "").toLowerCase().includes(searchLower) ||
      (task.description ?? "").toLowerCase().includes(searchLower),
  );
}

interface OnboardingTasksInboxSectionProps {
  tasks: OnboardingTask[];
  onOpenTask: (taskId: string) => void;
}

/**
 * The onboarding group of the inbox's pending list: open onboarding tasks and
 * UATs as clickable rows that open the task detail sheet. Renders nothing when
 * there are no tasks to show.
 */
export function OnboardingTasksInboxSection({
  tasks,
  onOpenTask,
}: OnboardingTasksInboxSectionProps) {
  if (tasks.length === 0) {
    return null;
  }

  return (
    <div className="px-6 pt-4">
      <div className="mb-2 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        <GraduationCap className="h-3.5 w-3.5" />
        Onboarding
        <span className="rounded-full bg-muted px-1.5 py-px text-[10px] tabular-nums">
          {tasks.length}
        </span>
      </div>
      <div className="flex flex-col gap-1 rounded-xl border bg-card p-1.5 shadow-sm">
        {tasks.map((task) => (
          <div
            key={task.id}
            className="group flex cursor-pointer items-center gap-3 rounded-md px-3 py-2.5 transition-all duration-75 hover:bg-muted/50"
            onClick={() => onOpenTask(task.id)}
          >
            <div className="h-2 w-2 shrink-0 rounded-full bg-amber-500" />
            <span className="min-w-0 flex-1 truncate text-sm font-medium">
              {task.name}
            </span>
            {task.type === ONBOARDING_TASK_TYPE.UAT && (
              <Badge variant="outline" className="shrink-0">
                UAT
              </Badge>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
